/**
 * FG Inward — Vendor-Site FG Phase 4D (Jun 2026, P0)
 *
 * Lists FG Inward bills (vendor-site finished goods invoices), supports
 * creating new bills against VENDOR-ownership branches, and exposes
 * retry-Zoho + cancel actions on the detail view.
 *
 * Backend: routes/fg_inward_routes.py (Phase 4B + 4C).
 *
 * Key UX rules baked in:
 *   1. Vendor dropdown only shows vendors that have at least one
 *      VENDOR-ownership branch — keeps the list operator-friendly.
 *   2. Branch is auto-selected from the chosen vendor (read-only).
 *   3. Per-line live variance preview pulls `VendorFGPrice` for the
 *      (vendor, branch, sku) tuple and shows |Δ%| vs the entered
 *      unit_price; >5% gets a HIGH pill, ≤5% a WITHIN pill.
 *   4. Counter strip across the top: PENDING_ZOHO / FAILED_ZOHO /
 *      PARTIAL / HIGH-variance counts, so finance can spot
 *      action-needed bills at a glance.
 *   5. PARTIAL bills show a red banner with "Cancel to recover" CTA —
 *      reverses the committed lines via Phase 4B snapshot-restore.
 *   6. FAILED Zoho push surfaces the exact error + a "Retry Zoho" button.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { asText } from "../lib/utils";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";
import {
  Plus, RefreshCw, AlertTriangle, FileText, Trash2,
  CheckCircle2, XCircle, Clock, X, Paperclip, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StockItemSearch from "../components/StockItemSearch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ──────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtDate = (d) => d ? String(d).slice(0, 10) : "—";

const StatusPill = ({ status, children, className = "" }) => {
  const palette = {
    // Feb 2026 — Zoho-webhook-driven lifecycle (parity with RM Inward):
    //   DRAFT     — bill committed locally, awaiting Zoho confirmation
    //   APPROVED  — Zoho confirmed the bill is real (green light)
    //   REJECTED  — Zoho voided the bill (setoff reversed or blocked)
    //   PARTIAL   — orthogonal: some lines committed, one failed
    //   POSTED    — LEGACY (pre-Feb-2026); backfill promotes → APPROVED
    //               or DRAFT depending on presence of zoho_bill_id.
    //   CANCELLED — operator-initiated
    APPROVED:  "bg-emerald-100 text-emerald-700 border-emerald-300",
    POSTED:    "bg-emerald-100 text-emerald-700 border-emerald-300",  // legacy
    DRAFT:     "bg-amber-100 text-amber-800 border-amber-300",
    PLAYFIELD_DRAFT: "bg-zinc-100 text-zinc-700 border-zinc-300",
    PUSH_PENDING:    "bg-blue-100 text-blue-700 border-blue-300",
    PUSH_FAILED:     "bg-rose-100 text-rose-700 border-rose-300",
    DELETED:         "bg-zinc-200 text-zinc-500 border-zinc-300 line-through",
    REJECTED:  "bg-rose-100 text-rose-700 border-rose-300",
    PARTIAL:   "bg-orange-100 text-orange-800 border-orange-300",
    CANCELLED: "bg-zinc-200 text-zinc-600 border-zinc-300",
    COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-300",
    PENDING:   "bg-blue-100 text-blue-700 border-blue-300",
    FAILED:    "bg-rose-100 text-rose-700 border-rose-300",
    SKIPPED:   "bg-zinc-100 text-zinc-500 border-zinc-300",
    WITHIN:    "bg-emerald-50 text-emerald-700 border-emerald-200",
    HIGH:      "bg-rose-50 text-rose-700 border-rose-200",
  };
  const cls = palette[status] || "bg-zinc-100 text-zinc-700 border-zinc-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono ${cls} ${className}`}>
      {children || status}
    </span>
  );
};

const CounterCard = ({ label, value, tone = "default", icon: Icon, onClick }) => {
  const tones = {
    default: "bg-zinc-50 border-zinc-200 text-zinc-700",
    warn:    "bg-amber-50 border-amber-200 text-amber-800",
    danger:  "bg-rose-50 border-rose-200 text-rose-700",
    info:    "bg-blue-50 border-blue-200 text-blue-700",
  };
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-lg border text-left transition hover:shadow-sm ${tones[tone] || tones.default}`}
      data-testid={`fg-inward-counter-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-xs uppercase font-mono tracking-wide opacity-70">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </button>
  );
};

// ── Page ─────────────────────────────────────────────────────────────
export default function FGInward() {
  const { token } = useAuthStore();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aging, setAging] = useState(null);                  // {buckets, totals} from /pending-aging
  const [agingDrill, setAgingDrill] = useState(null);        // bucket selected for drill-down
  const [filters, setFilters] = useState({ vendor_id: "", branch: "", status: "", variance_flag: "" });
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);

  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Feb 2026 — when set, the create dialog is in "edit draft" mode
  // and Save routes to PUT /fg-inward/bills/{id}/draft.
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [form, setForm] = useState(_blankForm());
  const [pricePreview, setPricePreview] = useState({});  // {sku_id: {price, variance_pct, flag}}

  // Detail dialog
  const [detailBill, setDetailBill] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Vendor invoice attachments (Phase 2, Jul 2026). Staged in memory on
  // the create dialog; uploaded individually after the bill posts so a
  // single failure doesn't abort the others. Mirrors RM Inward exactly.
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const ATTACH_TYPES = ".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf";
  const MAX_ATTACH_BYTES = 5 * 1024 * 1024;
  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = [];
    const rejected = [];
    for (const f of picked) {
      if (f.size > MAX_ATTACH_BYTES) {
        rejected.push(`${f.name} (>5 MB)`);
        continue;
      }
      valid.push(f);
    }
    if (rejected.length) toast.warning(`Skipped: ${rejected.join(", ")}`);
    setInvoiceFiles((prev) => [...prev, ...valid]);
    // Reset the input so picking the same file twice re-fires onChange.
    e.target.value = "";
  };
  const removeStagedFile = (idx) => {
    setInvoiceFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Zoho lookups (RM Inward parity, Phase 1) ────────────────────
  // Tax + GL account lists fetched once from existing /api/zoho/* endpoints
  // (same ones RM Inward uses). When Zoho isn't configured the calls 404
  // gracefully and we keep the dropdowns empty — local-only flow still
  // works since `tax_id` and `account_id` are optional on the BE.
  const [zohoTaxes, setZohoTaxes] = useState([]);
  const [zohoAccounts, setZohoAccounts] = useState([]); // expense accounts — same as RM Inward
  // Local TDS Taxes (Feb 2026 — RM Inward parity). Loaded from the same
  // `/api/tds-taxes` endpoint RM Inward uses so both bill flows share
  // the same catalogue and the Manage-TDS screen owns writes.
  const [tdsTaxes, setTdsTaxes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Match RM Inward exactly: same endpoint + same `account_type=expense`
        // filter so the FG account picker list is byte-identical to RM's.
        const [t, a, tds] = await Promise.all([
          axios.get(`${API}/zoho/taxes`, auth).catch(() => ({ data: [] })),
          axios.get(`${API}/zoho/accounts?account_type=expense`, auth).catch(() => ({ data: { accounts: [] } })),
          axios.get(`${API}/tds-taxes?status=ACTIVE`, auth).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setZohoTaxes(Array.isArray(t.data) ? t.data : (t.data?.taxes || t.data?.items || []));
        // RM Inward shape is `{ accounts: [...] }` — same here.
        setZohoAccounts(Array.isArray(a.data) ? a.data : (a.data?.accounts || a.data?.items || []));
        setTdsTaxes(Array.isArray(tds.data) ? tds.data : (tds.data?.items || []));
      } catch (e) {
        console.warn("Zoho lookups failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  // Auto-resolve a Zoho tax_id from the local code (e.g. "GST_18" → tax_id
  // whose tax_percentage === 18). Same heuristic as RMInward.js.
  const resolveTaxIdFromCode = useCallback((taxCode) => {
    if (!taxCode || taxCode === "NONE" || !zohoTaxes.length) return "";
    const pct = parseFloat(String(taxCode).replace(/[^\d.]/g, "")) || 0;
    const matches = zohoTaxes.filter(t => Math.abs(parseFloat(t.tax_percentage) - pct) < 0.001);
    if (!matches.length) return "";
    // Prefer a "group" tax (Zoho's GST groups combine CGST+SGST or IGST).
    const group = matches.find(t => (t.tax_type || "").toLowerCase().includes("group"));
    return (group || matches[0]).tax_id || "";
  }, [zohoTaxes]);

  // Default Inventory account id (option 1a). Strict precedence mirroring
  // RM Inward's "Raw Materials" lookup pattern: exact match → prefix
  // match → substring match. If nothing matches, leave blank — never
  // fall back to a placeholder string (that's what BUG was: "Default"
  // was sent verbatim and Zoho rejected the push).
  const defaultInventoryAccountId = useMemo(() => {
    if (!zohoAccounts.length) return "";
    const norm = (s) => (s || "").toLowerCase().trim();
    const acc =
      zohoAccounts.find(a => norm(a.account_name) === "inventory") ||
      zohoAccounts.find(a => norm(a.account_name).startsWith("inventory") && !norm(a.account_name).includes("consumable")) ||
      zohoAccounts.find(a => norm(a.account_name).includes("inventory"));
    return acc?.account_id || "";
  }, [zohoAccounts]);

  // Back-fill tax_id + account_id on lines whenever lookups land.
  useEffect(() => {
    if (!zohoTaxes.length && !defaultInventoryAccountId) return;
    setForm(prev => {
      const lines = prev.lines.map(l => {
        const next = { ...l };
        if (!next.tax_id && next.tax) next.tax_id = resolveTaxIdFromCode(next.tax);
        if (!next.account_id && defaultInventoryAccountId) next.account_id = defaultInventoryAccountId;
        return next;
      });
      return { ...prev, lines };
    });
  }, [zohoTaxes, defaultInventoryAccountId, resolveTaxIdFromCode]);

  // Auto-fill due_date from payment_terms (mirrors RM Inward).
  useEffect(() => {
    if (form.payment_terms === "CUSTOM") return;
    const term = PAYMENT_TERMS.find(t => t.value === form.payment_terms);
    if (!term || term.days == null) return;
    const base = new Date(form.bill_date || new Date());
    if (Number.isNaN(base.getTime())) return;
    const dd = new Date(base);
    dd.setDate(dd.getDate() + term.days);
    const iso = dd.toISOString().slice(0, 10);
    if (iso !== form.due_date) setForm(f => ({ ...f, due_date: iso }));
  }, [form.payment_terms, form.bill_date, form.due_date]);

  // Live totals derived from lines + bill-level discount.
  const totals = useMemo(() => {
    const sub_total = form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
    const discount_amount = form.discount_type === "percentage"
      ? Math.min(sub_total, sub_total * (Number(form.discount_value) || 0) / 100)
      : Math.min(sub_total, Number(form.discount_value) || 0);
    const taxable = Math.max(0, sub_total - discount_amount);
    const tax_total = form.lines.reduce((s, l) => {
      const lineSub = Number(l.qty || 0) * Number(l.unit_price || 0);
      const lineShare = sub_total > 0 ? lineSub / sub_total : 0;
      const taxRate = parseFloat(zohoTaxes.find(t => t.tax_id === l.tax_id)?.tax_percentage || String(l.tax).replace(/[^\d.]/g, "")) || 0;
      return s + (taxable * lineShare) * (taxRate / 100);
    }, 0);
    // ── RM Inward parity (Feb 2026) ────────────────────────────────
    // TDS deducted from grand_total, mirroring RMInward.js:281-298.
    // The selected `tds_tcs` is a local `db.tds_taxes.id`; the rate
    // comes from that row so we can render `-₹XX` and post the amount
    // pre-computed (BE recomputes defensively).
    const tdsRow = (form.tds_tcs && form.tds_tcs !== "NONE")
      ? tdsTaxes.find((t) => t.id === form.tds_tcs)
      : null;
    const tdsRate = tdsRow ? Number(tdsRow.rate) || 0 : 0;
    const tds_tcs_amount = taxable * (tdsRate / 100);
    const grand_total = taxable + tax_total - tds_tcs_amount;
    return {
      sub_total: round2(sub_total),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
      discount_amount: round2(discount_amount),
      tds_tcs: form.tds_tcs || "NONE",
      tds_tcs_amount: round2(tds_tcs_amount),
      tax_total: round2(tax_total),
      grand_total: round2(grand_total),
    };
  }, [form.lines, form.discount_type, form.discount_value, form.tds_tcs, zohoTaxes, tdsTaxes]);


  // ── Load lookups ────────────────────────────────────────────────
  const loadLookups = useCallback(async () => {
    try {
      const [v, b] = await Promise.all([
        axios.get(`${API}/vendors`, auth),
        axios.get(`${API}/branches`, auth),
      ]);
      setVendors(Array.isArray(v.data) ? v.data : (v.data?.items || []));
      setBranches(Array.isArray(b.data) ? b.data : (b.data?.items || []));
    } catch (e) {
      console.error("FGInward lookups failed", e);
    }
  }, [auth]);

  // ── Load bills ──────────────────────────────────────────────────
  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.vendor_id) params.vendor_id = filters.vendor_id;
      if (filters.branch) params.branch = filters.branch;
      if (filters.status) params.status = filters.status;
      if (filters.variance_flag) params.variance_flag = filters.variance_flag;
      params.page_size = 200;
      const r = await axios.get(`${API}/fg-inward/bills`, { ...auth, params });
      setBills(r.data?.items || []);
    } catch (e) {
      toast.error("Failed to load FG Inward bills");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [auth, filters]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadBills(); }, [loadBills]);

  // ── Aging widget (Phase 4E) — fetched whenever branch/vendor filters change.
  const loadAging = useCallback(async () => {
    try {
      const params = { include_rows: "true" };
      if (filters.branch) params.branch = filters.branch;
      if (filters.vendor_id) params.vendor_id = filters.vendor_id;
      const r = await axios.get(`${API}/fg-inward/pending-aging`, { ...auth, params });
      setAging(r.data);
    } catch (e) {
      console.warn("aging fetch failed", e);
      setAging(null);
    }
  }, [auth, filters.branch, filters.vendor_id]);
  useEffect(() => { loadAging(); }, [loadAging, bills.length]);

  // ── Derived state ───────────────────────────────────────────────
  // Vendor-site branches: a branch with `branch_type === "VENDOR_SITE"`.
  // Vendor-FG flows (cutover, FG-Inward bills, pending intake) only apply
  // to these. `branch_type` is the single source of truth (Jun 2026 cleanup).
  const vendorBranches = useMemo(
    () => branches.filter((b) => (b?.branch_type || "").toUpperCase() === "VENDOR_SITE"),
    [branches]
  );

  const vendorBranchMap = useMemo(() => {
    const m = {};
    for (const br of vendorBranches) {
      if (br.vendor_id) {
        if (!m[br.vendor_id]) m[br.vendor_id] = [];
        m[br.vendor_id].push(br);
      }
    }
    return m;
  }, [vendorBranches]);

  const eligibleVendors = useMemo(
    () => vendors.filter((v) => vendorBranchMap[v.vendor_id]?.length),
    [vendors, vendorBranchMap]
  );

  const counters = useMemo(() => {
    let pendingZ = 0, failedZ = 0, partial = 0, highVar = 0;
    for (const b of bills) {
      if (b.zoho_push_status === "PENDING") pendingZ++;
      if (b.zoho_push_status === "FAILED") failedZ++;
      if (b.status === "PARTIAL") partial++;
      if (b.variance_flag === "HIGH") highVar++;
    }
    return { pendingZ, failedZ, partial, highVar };
  }, [bills]);

  // ── Form handlers ───────────────────────────────────────────────
  const openCreate = () => {
    // Pre-seed first line with resolved Zoho ids so the Account dropdown
    // lands on "Inventory" and Tax on the matched GST group from the very
    // first render. The back-fill `useEffect` only re-runs when its lookup
    // deps change — once those are stable, opening a fresh dialog with a
    // blank form leaves the line at "" → "Default" / "None". Baking the
    // ids in here is the deterministic fix.
    const blank = _blankForm();
    blank.lines = blank.lines.map(l => ({
      ...l,
      tax_id: l.tax_id || resolveTaxIdFromCode(l.tax || "GST_18"),
      account_id: l.account_id || defaultInventoryAccountId,
    }));
    setForm(blank);
    setPricePreview({});
    setShowCreate(true);
  };

  const onVendorChange = (vendor_id) => {
    const candidates = vendorBranchMap[vendor_id] || [];
    setForm((f) => ({ ...f, vendor_id, branch: candidates[0]?.name || "" }));
  };

  const fetchPricePreview = useCallback(async (sku_id, vendor_id, branch) => {
    if (!sku_id || !vendor_id) return;
    try {
      const r = await axios.get(`${API}/vendor-fg-prices`, {
        ...auth,
        params: { vendor_id, buyer_sku_id: sku_id },
      });
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Branch-specific first; else default (branch === null).
      const branchHit = list.find(p => p.branch === branch);
      const def = list.find(p => p.branch == null);
      const rate = branchHit?.price ?? def?.price ?? null;
      setPricePreview(prev => ({ ...prev, [sku_id]: { rate, source: branchHit ? "branch" : (def ? "default" : "none") } }));
    } catch (e) {
      console.warn("price preview failed", e);
    }
  }, [auth]);

  const setLine = (idx, patch) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...f, lines };
    });
    if (patch.buyer_sku_id) {
      fetchPricePreview(patch.buyer_sku_id, form.vendor_id, form.branch);
    }
  };

  const addLine = () => setForm(f => ({
    ...f,
    lines: [
      ...f.lines,
      {
        buyer_sku_id: "", description: "", hsn: "",
        qty: "", unit_price: "",
        tax: "GST_18", tax_id: resolveTaxIdFromCode("GST_18"),
        account_id: defaultInventoryAccountId,
        amount: 0,
      },
    ],
  }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const submitBill = async ({ asDraft = false } = {}) => {
    if (!form.vendor_id || !form.branch) { toast.error("Vendor & branch are required"); return; }
    if (!form.bill_number?.trim()) { toast.error("Bill number is required"); return; }
    const cleanLines = form.lines
      .filter(l => l.buyer_sku_id && Number(l.qty) > 0 && Number(l.unit_price) >= 0)
      .map(l => {
        const amount = round2(Number(l.qty) * Number(l.unit_price));
        const taxRate = parseFloat(
          zohoTaxes.find(t => t.tax_id === l.tax_id)?.tax_percentage
          || String(l.tax || "").replace(/[^\d.]/g, "")
        ) || 0;
        return {
          buyer_sku_id: l.buyer_sku_id,
          qty: Number(l.qty),
          unit_price: Number(l.unit_price),
          description: asText(l.description),
          hsn: asText(l.hsn),
          tax: l.tax || undefined,
          tax_id: l.tax_id || undefined,
          tax_amount: round2(amount * taxRate / 100),
          account_id: l.account_id || undefined,
          amount,
        };
      });
    if (!cleanLines.length) { toast.error("At least one valid line is required"); return; }
    // Block save when Zoho is configured but a line is missing tax_id —
    // mirrors RM Inward's hard guard.
    if (zohoTaxes.length > 0) {
      const missing = cleanLines.find(l => !l.tax_id);
      if (missing) {
        toast.error(`Pick a tax for line ${cleanLines.indexOf(missing) + 1} — Zoho requires a tax_id.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const body = {
        bill_number: form.bill_number.trim(),
        // Feb 2026 — RM Inward parity: optional Order Number / PO ref.
        order_number: (form.order_number || "").trim() || undefined,
        bill_date: form.bill_date || new Date().toISOString().slice(0, 10),
        due_date: form.due_date || null,
        payment_terms: form.payment_terms || "DUE_ON_RECEIPT",
        accounts_payable: form.accounts_payable || "Trade Payables",
        vendor_id: form.vendor_id,
        branch: form.branch,
        lines: cleanLines,
        totals,
        notes: form.notes || "",
      };
      const r = editingDraftId
        ? await axios.put(
            `${API}/fg-inward/bills/${editingDraftId}/draft`, body, auth,
          )
        : await axios.post(
            `${API}/fg-inward/bills${asDraft ? "/draft" : ""}`, body, auth,
          );
      const bill = r.data;
      // Phase 2 — upload staged attachments individually, post-create.
      // Each file POSTed separately so one failure doesn't abort the rest.
      const newBillId = bill?.id || bill?.bill_id;
      if (invoiceFiles.length > 0 && newBillId) {
        let okCount = 0, failCount = 0, zohoSyncedCount = 0;
        const failMsgs = [];
        for (const f of invoiceFiles) {
          try {
            const fd = new FormData();
            fd.append("file", f);
            const upResp = await axios.post(
              `${API}/fg-inward/bills/${newBillId}/attachment`,
              fd,
              { ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" } }
            );
            okCount += 1;
            if (upResp.data?.zoho_sync?.zoho_synced) zohoSyncedCount += 1;
          } catch (uploadErr) {
            failCount += 1;
            failMsgs.push(`${f.name}: ${uploadErr?.response?.data?.detail || "upload failed"}`);
          }
        }
        if (okCount > 0 && failCount === 0) {
          toast.success(
            zohoSyncedCount === okCount
              ? `${okCount} attachment(s) saved & synced to Zoho.`
              : `${okCount} attachment(s) saved${zohoSyncedCount ? ` (${zohoSyncedCount} synced)` : ""}.`,
            { duration: 5000 }
          );
        } else if (okCount > 0 && failCount > 0) {
          toast.warning(`${okCount} attached, ${failCount} failed: ${failMsgs.join("; ")}`, { duration: 8000 });
        } else if (failCount > 0) {
          toast.error(`Bill saved, but attachments failed: ${failMsgs.join("; ")}`, { duration: 10000 });
        }
      }
      if (bill.status === "PARTIAL") {
        toast.warning(`Bill posted PARTIAL — line ${bill.partial_failure?.failed_line_index + 1} failed. Cancel to recover.`);
      } else {
        // Feb 2026 — bills land as DRAFT and are promoted to APPROVED by
        // the shared /api/zoho/webhook/bill dispatcher when Zoho confirms.
        // The HTTP push here just registers the bill in Zoho; the lifecycle
        // flip happens on the Zoho webhook return trip.
        const zMsg = bill.zoho_push_status === "COMPLETED" ? "Zoho invoiced — awaiting Approval webhook."
                    : bill.zoho_push_status === "FAILED" ? "Local DRAFT. Zoho push FAILED — retry available."
                    : bill.zoho_push_status === "SKIPPED" ? "Local DRAFT. Zoho skipped (not configured)."
                    : "Local DRAFT. Zoho push pending…";
        toast.success(`Bill ${bill.bill_number} saved. ${zMsg}`);
      }
      setInvoiceFiles([]);
      setShowCreate(false);
      setEditingDraftId(null);
      loadBills();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.bill_id) {
        toast.error(`${detail.error} — bill persisted as PARTIAL (id=${detail.bill_id}). Cancel to recover.`);
        loadBills();
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.error || "Failed to save bill"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Actions on existing bill ────────────────────────────────────
  const retryZoho = async (billId) => {
    try {
      const r = await axios.post(`${API}/fg-inward/bills/${billId}/retry-zoho`, {}, auth);
      const status = r.data?.zoho_push_status;
      toast.success(`Retry → ${status}${status === "COMPLETED" && r.data?.zoho_bill_id ? ` (${r.data.zoho_bill_id})` : ""}`);
      setDetailBill(r.data);
      loadBills();
    } catch (e) {
      toast.error(formatErr(e, "Retry failed"));
    }
  };

  // ── Attachment → Zoho sync (Feb 2026) ────────────────────────────
  // Mirrors RM Inward's `RMInwardBillDetailDialog.handleSyncAttachmentToZoho`
  // but for FG's array-only schema. Reports the endpoint's per-attachment
  // `synced/failed/skipped` summary (AC5) rather than just a doc_id, then
  // refetches the bill so the badges update.
  const [zohoAttSyncing, setZohoAttSyncing] = useState(false);
  const handleSyncFgAttachmentsToZoho = async () => {
    if (!detailBill?.id) return;
    setZohoAttSyncing(true);
    try {
      const res = await axios.post(
        `${API}/fg-inward/bills/${encodeURIComponent(detailBill.id)}/attachment/sync-to-zoho`,
        null, auth,
      );
      const { synced = 0, failed = 0, skipped = 0, results = [] } = res.data || {};
      const firstErr = (results.find((r) => !r.synced && r.error) || {}).error;
      if (failed === 0 && synced > 0) {
        toast.success(`${synced} attachment${synced === 1 ? "" : "s"} synced to Zoho`);
      } else if (synced > 0 && failed > 0) {
        toast.warning(`${synced} synced, ${failed} failed — ${firstErr || "see details"}`);
      } else if (failed > 0) {
        toast.error(`${failed} attachment${failed === 1 ? "" : "s"} failed — ${firstErr || "see details"}`);
      } else {
        toast.info(`Nothing to sync (${skipped} skipped)`);
      }
      // Refetch so badges reflect the new state.
      const fresh = await axios.get(`${API}/fg-inward/bills/${encodeURIComponent(detailBill.id)}`, auth);
      setDetailBill(fresh.data);
    } catch (e) {
      toast.error(formatErr(e, "Failed to sync attachments to Zoho"));
    } finally {
      setZohoAttSyncing(false);
    }
  };

  // Feb 2026 — draft-lifecycle actions on the detail dialog.
  const [draftBusy, setDraftBusy] = useState(false);
  const handleSubmitDraft = async () => {
    if (!detailBill?.id) return;
    setDraftBusy(true);
    try {
      const r = await axios.post(
        `${API}/fg-inward/bills/${detailBill.id}/submit`, {}, auth,
      );
      toast.success(`Submitted — status now ${r.data.status}`);
      setDetailBill(r.data);
      loadBills();
    } catch (e) {
      toast.error(`Submit failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDraftBusy(false);
    }
  };
  const handleSoftDelete = async () => {
    if (!detailBill?.id) return;
    const reason = window.prompt(
      `Delete bill ${detailBill.bill_number}? This retires it — the bill number frees up for re-use. Reason (optional):`,
      "",
    );
    if (reason === null) return;   // user hit Cancel
    setDraftBusy(true);
    try {
      const r = await axios.post(
        `${API}/fg-inward/bills/${detailBill.id}/soft-delete`,
        { reason: reason || null }, auth,
      );
      toast.success(r.data?.message || "Bill deleted.");
      setDetailBill(null);
      loadBills();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDraftBusy(false);
    }
  };


  const submitCancel = async () => {
    if (!detailBill) return;
    try {
      const r = await axios.post(
        `${API}/fg-inward/bills/${detailBill.id}/cancel`,
        { reason: cancelReason || "" },
        auth,
      );
      toast.success(`Bill ${r.data.bill_number} cancelled — inventory restored.`);
      setConfirmCancel(false);
      setDetailBill(r.data);
      setCancelReason("");
      loadBills();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.blocking_links) {
        toast.error("Blocked — later invoices have drawn from the same pending intakes. Cancel them first.");
      } else {
        toast.error(formatErr(e, "Cancel failed"));
      }
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6" data-testid="fg-inward-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FG Inward Invoices</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Vendor-site finished goods invoicing — FIFO setoff against pending intakes + MWAC blend.
          </p>
        </div>
        <PermButton
          entity="FGInwardBill" action="CREATE"
          onClick={openCreate}
          className="gap-2"
          data-testid="fg-inward-new-bill-btn"
        >
          <Plus className="w-4 h-4" /> New FG Inward Bill
        </PermButton>
      </div>

      {/* Counter strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CounterCard label="Pending Zoho push" value={counters.pendingZ} tone="info"   icon={Clock}
          onClick={() => setFilters(f => ({ ...f, status: "" }))} />
        <CounterCard label="Failed Zoho push"  value={counters.failedZ}  tone="danger" icon={XCircle}
          onClick={() => setFilters(f => ({ ...f, status: "" }))} />
        <CounterCard label="Partial bills"     value={counters.partial}  tone="warn"   icon={AlertTriangle}
          onClick={() => setFilters(f => ({ ...f, status: "PARTIAL" }))} />
        <CounterCard label="High variance"     value={counters.highVar}  tone="warn"   icon={AlertTriangle}
          onClick={() => setFilters(f => ({ ...f, variance_flag: "HIGH" }))} />
      </div>

      {/* Aging widget (Phase 4E) — pending FG awaiting invoice */}
      {aging && aging.totals.count > 0 && (
        <div className="border rounded-lg p-4 bg-gradient-to-r from-amber-50 to-rose-50" data-testid="fg-inward-aging-widget">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide">Pending invoice aging</h2>
              <p className="text-xs text-zinc-600 mt-0.5">
                Vendor-site FG produced but not yet invoiced.
                {filters.branch ? <> Branch: <strong>{filters.branch}</strong>.</> : <> All vendor branches.</>}
                {" "}Total <strong>{fmt(aging.totals.qty)} units</strong> {" "}
                worth approx. <strong>₹ {fmt(aging.totals.value_at_master_rate)}</strong> at master rate
                {aging.totals.qty_no_master > 0 && <> ({fmt(aging.totals.qty_no_master)} units missing master price)</>}.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "0-7",   tone: "default" },
              { key: "8-30",  tone: "info" },
              { key: "31-60", tone: "warn" },
              { key: "60+",   tone: "danger" },
            ].map(({ key, tone }) => {
              const b = aging.buckets[key];
              const palette = {
                default: "bg-white border-zinc-200",
                info:    "bg-blue-50 border-blue-200",
                warn:    "bg-amber-50 border-amber-300",
                danger:  "bg-rose-50 border-rose-300",
              };
              return (
                <button
                  key={key}
                  onClick={() => b.count > 0 && setAgingDrill({ key, label: b.label, rows: b.rows || [] })}
                  className={`p-3 rounded-lg border text-left transition hover:shadow-sm disabled:opacity-50 disabled:cursor-default ${palette[tone]}`}
                  disabled={b.count === 0}
                  data-testid={`fg-inward-aging-bucket-${key}`}
                >
                  <div className="text-xs uppercase font-mono opacity-70">{b.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold">{fmt(b.qty)}</span>
                    <span className="text-xs text-zinc-500">units</span>
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    {b.count} {b.count === 1 ? "intake" : "intakes"}
                    {b.value_at_master_rate > 0 && <> · ₹ {fmt(b.value_at_master_rate)}</>}
                  </div>
                  {b.qty_no_master > 0 && (
                    <div className="text-xs text-amber-700 mt-0.5">⚠ {fmt(b.qty_no_master)} units missing price</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[200px]">
          <Label className="text-xs">Vendor</Label>
          <Select value={filters.vendor_id || "ALL"} onValueChange={(v) => setFilters(f => ({ ...f, vendor_id: v === "ALL" ? "" : v }))}>
            <SelectTrigger data-testid="fg-inward-filter-vendor"><SelectValue placeholder="All vendors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All vendors</SelectItem>
              {eligibleVendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs">Status</Label>
          <Select value={filters.status || "ALL"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "ALL" ? "" : v }))}>
            <SelectTrigger data-testid="fg-inward-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="PLAYFIELD_DRAFT">PLAYFIELD_DRAFT</SelectItem>
              <SelectItem value="DRAFT">DRAFT</SelectItem>
              <SelectItem value="APPROVED">APPROVED</SelectItem>
              <SelectItem value="PUSH_PENDING">PUSH_PENDING</SelectItem>
              <SelectItem value="PUSH_FAILED">PUSH_FAILED</SelectItem>
              <SelectItem value="REJECTED">REJECTED</SelectItem>
              <SelectItem value="PARTIAL">PARTIAL</SelectItem>
              <SelectItem value="POSTED">POSTED (legacy)</SelectItem>
              <SelectItem value="CANCELLED">CANCELLED</SelectItem>
              <SelectItem value="DELETED">DELETED</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Variance</Label>
          <Select value={filters.variance_flag || "ALL"} onValueChange={(v) => setFilters(f => ({ ...f, variance_flag: v === "ALL" ? "" : v }))}>
            <SelectTrigger data-testid="fg-inward-filter-variance"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any</SelectItem>
              <SelectItem value="WITHIN">WITHIN</SelectItem>
              <SelectItem value="HIGH">HIGH</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" onClick={loadBills} className="gap-1" data-testid="fg-inward-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* List */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="fg-inward-list-table">
          <thead className="bg-zinc-100">
            <tr>
              <th className="p-3 text-left font-mono text-xs uppercase">Bill #</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Date</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Vendor</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Branch</th>
              <th className="p-3 text-right font-mono text-xs uppercase">Qty</th>
              <th className="p-3 text-right font-mono text-xs uppercase">Value</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Status</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Zoho</th>
              <th className="p-3 text-left font-mono text-xs uppercase">Variance</th>
              <th className="p-3 text-right font-mono text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-6 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && !bills.length && <tr><td colSpan={10} className="p-6 text-center text-zinc-500">No FG Inward bills match the filters.</td></tr>}
            {bills.map(b => (
              <tr key={b.id} className="border-t hover:bg-zinc-50 transition" data-testid={`fg-inward-row-${b.id}`}>
                <td className="p-3 font-mono text-xs">{b.bill_number}</td>
                <td className="p-3">{fmtDate(b.bill_date)}</td>
                <td className="p-3">{b.vendor_name || b.vendor_id}</td>
                <td className="p-3">{b.branch}</td>
                <td className="p-3 text-right font-mono">{fmt(b.total_qty)}</td>
                <td className="p-3 text-right font-mono">₹ {fmt(b.total_value)}</td>
                <td className="p-3"><StatusPill status={b.status} /></td>
                <td className="p-3">{b.zoho_push_status && b.zoho_push_status !== "COMPLETED" ? <StatusPill status={b.zoho_push_status} /> : (b.zoho_bill_id ? <StatusPill status="COMPLETED">Pushed</StatusPill> : "—")}</td>
                <td className="p-3">{b.variance_flag ? <StatusPill status={b.variance_flag} /> : "—"}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetailBill(b)} data-testid={`fg-inward-detail-${b.id}`}>
                    <FileText className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Dialog — RM Inward parity (Phase 1, Jul 2026) */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="fg-inward-create-dialog">
          <DialogHeader><DialogTitle>New FG Inward Bill</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Header row 1: bill identifiers + vendor */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Bill Number *</Label>
                <Input value={form.bill_number} onChange={(e) => setForm(f => ({ ...f, bill_number: e.target.value }))} placeholder="VEN/2026/001" data-testid="fg-inward-bill-number" />
              </div>
              <div>
                <Label>Bill Date *</Label>
                <Input type="date" value={form.bill_date?.slice(0, 10) || ""} onChange={(e) => setForm(f => ({ ...f, bill_date: e.target.value }))} data-testid="fg-inward-bill-date" />
              </div>
              <div>
                <Label>Vendor *</Label>
                <Select value={form.vendor_id || "_"} onValueChange={onVendorChange}>
                  <SelectTrigger data-testid="fg-inward-vendor-select"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
                  <SelectContent>
                    {eligibleVendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Header row 2: payment terms / due date / accounts payable */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Order Number</Label>
                <Input
                  value={form.order_number || ""}
                  onChange={(e) => setForm(f => ({ ...f, order_number: e.target.value }))}
                  placeholder="PO reference (optional)"
                  className="font-mono"
                  data-testid="fg-inward-order-number"
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setForm(f => ({ ...f, payment_terms: v }))}>
                  <SelectTrigger data-testid="fg-inward-payment-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={form.due_date?.slice(0, 10) || ""}
                  onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value, payment_terms: "CUSTOM" }))}
                  data-testid="fg-inward-due-date"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Accounts Payable</Label>
                <Input
                  value={form.accounts_payable}
                  onChange={(e) => setForm(f => ({ ...f, accounts_payable: e.target.value }))}
                  data-testid="fg-inward-accounts-payable"
                />
                <div className="text-xs text-zinc-500 mt-0.5">Default: Trade Payables</div>
              </div>
            </div>

            <div>
              <Label>Branch (vendor-site)</Label>
              <Input value={form.branch} readOnly className="bg-zinc-50" data-testid="fg-inward-branch" />
              {form.vendor_id && (vendorBranchMap[form.vendor_id]?.length || 0) > 1 && (
                <Select value={form.branch || ""} onValueChange={(v) => setForm((f) => ({ ...f, branch: v }))}>
                  <SelectTrigger className="mt-1" data-testid="fg-inward-branch-select"><SelectValue placeholder="Pick branch" /></SelectTrigger>
                  <SelectContent>
                    {(vendorBranchMap[form.vendor_id] || []).map((br) => (
                      <SelectItem key={br.name} value={br.name}>{br.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Line Items</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="fg-inward-add-line"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100">
                    <tr>
                      <th className="p-2 text-left font-mono text-xs">Buyer SKU</th>
                      <th className="p-2 text-right font-mono text-xs w-20">Qty</th>
                      <th className="p-2 text-right font-mono text-xs w-28">Unit ₹</th>
                      <th className="p-2 text-left font-mono text-xs w-28">Tax</th>
                      <th className="p-2 text-left font-mono text-xs w-32">Account</th>
                      <th className="p-2 text-right font-mono text-xs w-24">Line Total</th>
                      <th className="p-2 text-right font-mono text-xs w-28">Variance</th>
                      <th className="p-2 text-right w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, idx) => {
                      const pp = pricePreview[ln.buyer_sku_id];
                      const masterRate = pp?.rate;
                      const ratio = (masterRate && ln.unit_price) ? (Number(ln.unit_price) - masterRate) / masterRate * 100 : null;
                      const flag = ratio == null ? null : (Math.abs(ratio) <= 5 ? "WITHIN" : "HIGH");
                      const excludeIds = form.lines
                        .map((l, i) => i !== idx ? l.buyer_sku_id : null)
                        .filter(Boolean);
                      const canSearch = !!(form.vendor_id && form.branch);
                      const searchEndpoint = canSearch
                        ? `${API}/fg-inward/pending-invoice-skus?branch=${encodeURIComponent(form.branch)}&vendor_id=${encodeURIComponent(form.vendor_id)}`
                        : undefined;
                      const lineAmount = round2(Number(ln.qty || 0) * Number(ln.unit_price || 0));
                      return (
                        <tr key={idx} className="border-t">
                          <td className="p-2">
                            <StockItemSearch
                              searchEndpoint={searchEndpoint}
                              value={ln.buyer_sku_id}
                              onSelect={(item) => setLine(idx, { buyer_sku_id: item.item_id || "", description: item.label || item.description || "" })}
                              disabled={!canSearch}
                              placeholder={canSearch
                                ? "Type 2+ chars to search SKUs pending invoice…"
                                : "Pick vendor & branch first"}
                              excludeIds={excludeIds}
                              minChars={2}
                              testId={`fg-inward-line-sku-search-${idx}`}
                            />
                          </td>
                          <td className="p-2">
                            <Input type="number" value={ln.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} className="no-spinner text-right" onWheel={(e) => e.currentTarget.blur()} data-testid={`fg-inward-line-qty-${idx}`} />
                          </td>
                          <td className="p-2">
                            <Input type="number" step="0.01" value={ln.unit_price} onChange={(e) => setLine(idx, { unit_price: e.target.value })} className="no-spinner text-right" onWheel={(e) => e.currentTarget.blur()} data-testid={`fg-inward-line-price-${idx}`} />
                          </td>
                          <td className="p-2">
                            <Select
                              value={ln.tax_id || "_"}
                              onValueChange={(v) => {
                                const t = zohoTaxes.find(x => x.tax_id === v);
                                const code = t ? `GST_${Math.round(parseFloat(t.tax_percentage))}` : ln.tax;
                                setLine(idx, { tax_id: v === "_" ? "" : v, tax: code });
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`fg-inward-line-tax-${idx}`}>
                                <SelectValue placeholder={zohoTaxes.length ? "Pick tax" : "(no Zoho)"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_">None</SelectItem>
                                {zohoTaxes.map(t => (
                                  <SelectItem key={t.tax_id} value={t.tax_id}>
                                    {t.tax_name} ({t.tax_percentage}%)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Select
                              value={ln.account_id || "_"}
                              onValueChange={(v) => setLine(idx, { account_id: v === "_" ? "" : v })}
                            >
                              <SelectTrigger className="h-8 text-xs" data-testid={`fg-inward-line-account-${idx}`}>
                                <SelectValue placeholder={zohoAccounts.length ? "Inventory" : "(no Zoho)"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_">Default</SelectItem>
                                {zohoAccounts.map(a => (
                                  <SelectItem key={a.account_id} value={a.account_id}>{a.account_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2 text-right font-mono text-xs" data-testid={`fg-inward-line-amount-${idx}`}>
                            ₹ {fmt(lineAmount)}
                          </td>
                          <td className="p-2 text-right text-xs">
                            {masterRate == null ? <span className="text-zinc-400">no master</span> : (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="font-mono">₹ {fmt(masterRate)}</span>
                                {ratio != null && <StatusPill status={flag}>{ratio > 0 ? "+" : ""}{ratio.toFixed(2)}%</StatusPill>}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => removeLine(idx)}><X className="w-3 h-3" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes (bill-level) */}
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="fg-inward-notes" />
            </div>

            {/* Totals strip — RM Inward parity: inline discount row */}
            <div className="space-y-3 bg-gray-50 p-4 rounded-lg" data-testid="fg-inward-totals">
              <div className="flex justify-between text-sm">
                <span>Sub Total</span>
                <span className="font-mono font-medium" data-testid="fg-inward-sub-total">₹{totals.sub_total.toFixed(2)}</span>
              </div>

              {/* Discount — compact inline row (% / ₹ selector | numeric input | computed deduction) */}
              <div className="flex items-center gap-2">
                <span className="text-sm w-20">Discount</span>
                <Select
                  value={form.discount_type || undefined}
                  onValueChange={(v) => setForm(f => ({ ...f, discount_type: v }))}
                >
                  <SelectTrigger className="w-20 h-8" data-testid="fg-inward-discount-type">
                    <SelectValue placeholder="%" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">%</SelectItem>
                    <SelectItem value="amount">₹</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discount_value}
                  onChange={(e) => setForm(f => ({ ...f, discount_value: parseFloat(e.target.value) || 0 }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="no-spinner w-24 h-8 text-right font-mono"
                  data-testid="fg-inward-discount-value"
                />
                <span className="font-mono text-sm ml-auto" data-testid="fg-inward-discount-amount">
                  -₹{totals.discount_amount.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span>Tax Total</span>
                <span className="font-mono" data-testid="fg-inward-tax-total">+₹{totals.tax_total.toFixed(2)}</span>
              </div>

              {/* TDS/TCS — RM Inward parity (Feb 2026). Same catalogue,
                  same "None" sentinel, same amount format. */}
              <div className="flex items-center gap-2">
                <span className="text-sm w-20">TDS</span>
                <Select
                  value={form.tds_tcs || "NONE"}
                  onValueChange={(v) => setForm(f => ({ ...f, tds_tcs: v }))}
                >
                  <SelectTrigger className="w-44 h-8" data-testid="fg-inward-tds-select-trigger">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">None</SelectItem>
                    {tdsTaxes.map(t => (
                      <SelectItem
                        key={t.id}
                        value={t.id}
                        data-testid={`fg-inward-tds-option-${t.id}`}
                      >
                        {t.label || `${t.tax_name} ${t.rate}%`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="font-mono text-sm ml-auto" data-testid="fg-inward-tds-amount">
                  -₹{totals.tds_tcs_amount.toFixed(2)}
                </span>
              </div>

              <Separator />

              <div className="flex justify-between text-lg font-bold">
                <span>Grand Total</span>
                <span className="font-mono text-primary" data-testid="fg-inward-grand-total">
                  ₹{totals.grand_total.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Vendor invoice attachments (Phase 2) */}
            <div className="border rounded-lg p-3 bg-zinc-50 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Vendor invoice attachments</Label>
                <div className="text-xs text-zinc-500">JPEG · PNG · WebP · HEIC · PDF · ≤ 5 MB each</div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  accept={ATTACH_TYPES}
                  onChange={onFilesPicked}
                  className="text-sm"
                  data-testid="fg-inward-attachment-input"
                />
                {invoiceFiles.length > 0 && (
                  <span className="text-xs text-zinc-600">{invoiceFiles.length} file(s) staged</span>
                )}
              </div>
              {invoiceFiles.length > 0 && (
                <ul className="space-y-1" data-testid="fg-inward-attachment-list">
                  {invoiceFiles.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-sm bg-white rounded border px-2 py-1">
                      <span className="font-mono text-xs truncate" title={f.name}>{f.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-zinc-500">{(f.size / 1024).toFixed(1)} KB</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeStagedFile(i)}
                          data-testid={`fg-inward-attachment-remove-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setEditingDraftId(null); }} disabled={submitting}>Cancel</Button>
              <Button
                variant="outline"
                onClick={() => submitBill({ asDraft: true })}
                disabled={submitting}
                data-testid="fg-inward-save-draft"
                title="Save without pushing to Zoho — editable until you submit"
              >
                {submitting ? "Saving…" : (editingDraftId ? "Save changes" : "Save as draft")}
              </Button>
              {!editingDraftId && (
                <Button onClick={() => submitBill()} disabled={submitting} data-testid="fg-inward-submit">
                  {submitting ? "Saving…" : "Save & invoice FG"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailBill} onOpenChange={(open) => { if (!open) setDetailBill(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fg-inward-detail-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {detailBill?.bill_number}</DialogTitle></DialogHeader>
          {detailBill && (
            <div className="space-y-4">
              {/* PARTIAL banner */}
              {detailBill.status === "PARTIAL" && (
                <div className="p-3 border border-amber-300 bg-amber-50 rounded-lg flex items-start gap-2" data-testid="fg-inward-partial-banner">
                  <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold text-amber-900">PARTIAL — bill saved with committed lines only.</div>
                    <div className="text-amber-800 mt-1">
                      Line {detailBill.partial_failure?.failed_line_index + 1} ({detailBill.partial_failure?.failed_buyer_sku_id}) failed: <em>{detailBill.partial_failure?.error_message}</em>
                    </div>
                    <div className="text-amber-800 mt-1">Cancel this bill to reverse the committed lines, then resubmit a clean bill.</div>
                  </div>
                </div>
              )}
              {/* FAILED Zoho banner */}
              {detailBill.zoho_push_status === "FAILED" && (
                <div className="p-3 border border-rose-300 bg-rose-50 rounded-lg flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-rose-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm flex-1">
                    <div className="font-semibold text-rose-900">Zoho push FAILED</div>
                    <div className="text-rose-800 mt-1 font-mono text-xs break-all">{detailBill.zoho_last_error}</div>
                    <div className="mt-2">
                      <PermButton entity="FGInwardBill" action="UPDATE" size="sm" onClick={() => retryZoho(detailBill.id)} className="gap-1" data-testid="fg-inward-retry-zoho">
                        <RefreshCw className="w-3.5 h-3.5" /> Retry Zoho Push
                      </PermButton>
                    </div>
                  </div>
                </div>
              )}
              {/* Attachments — Feb 2026 per-attachment Zoho sync status
                  (mirrors RM Inward's `RMInwardBillDetailDialog`). */}
              {Array.isArray(detailBill.attachments) && detailBill.attachments.filter((a) => a && !a.is_deleted).length > 0 && (() => {
                const active = detailBill.attachments.filter((a) => a && !a.is_deleted);
                const anyUnsynced = active.some((a) => !a.zoho_synced);
                return (
                  <div className="p-3 border rounded-lg" data-testid="fg-attachments-block">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                        <Paperclip className="w-3 h-3" /> Attachments ({active.length})
                      </div>
                      {detailBill.zoho_bill_id && anyUnsynced && (
                        <PermButton
                          entity="FGInwardBill" action="UPDATE"
                          variant="outline" size="sm"
                          onClick={handleSyncFgAttachmentsToZoho}
                          disabled={zohoAttSyncing}
                          data-testid="fg-attachment-sync-to-zoho-btn"
                        >
                          {zohoAttSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Sync to Zoho
                        </PermButton>
                      )}
                    </div>
                    <div className="space-y-1">
                      {active.map((a) => (
                        <div
                          key={a.id || a.storage_path}
                          className="flex items-center justify-between gap-2 bg-zinc-50 border rounded px-2 py-1.5 text-[11px]"
                          data-testid={`fg-attachment-row-${a.id || ""}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Paperclip className="w-3 h-3 text-zinc-400 shrink-0" />
                            <span className="truncate text-zinc-800 font-medium" title={a.original_filename}>
                              {a.original_filename || "(unnamed)"}
                            </span>
                            <span className="text-zinc-500 whitespace-nowrap font-mono text-[10px]">
                              {(Number(a.size || 0) / 1024).toFixed(0)} KB
                            </span>
                          </div>
                          {detailBill.zoho_bill_id && (
                            a.zoho_synced ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200"
                                title={`Zoho doc: ${a.zoho_document_id || "?"}`}
                                data-testid={`fg-attachment-zoho-badge-synced-${a.id}`}
                              >
                                <CheckCircle2 className="w-2.5 h-2.5" /> Synced
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                                title={a.zoho_last_error || "Not yet synced to Zoho"}
                                data-testid={`fg-attachment-zoho-badge-pending-${a.id}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5" /> Not synced
                              </span>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Header summary */}
              <div className="grid grid-cols-2 gap-3 text-sm p-3 bg-zinc-50 rounded-lg border">
                <div><span className="text-zinc-500">Status:</span> <StatusPill status={detailBill.status} /></div>
                <div><span className="text-zinc-500">Variance:</span> {detailBill.variance_flag ? <StatusPill status={detailBill.variance_flag} /> : "—"}</div>
                <div><span className="text-zinc-500">Vendor:</span> {detailBill.vendor_name}</div>
                <div><span className="text-zinc-500">Branch:</span> {detailBill.branch}</div>
                <div><span className="text-zinc-500">Bill date:</span> {fmtDate(detailBill.bill_date)}</div>
                <div><span className="text-zinc-500">Zoho:</span> {detailBill.zoho_push_status ? <StatusPill status={detailBill.zoho_push_status} /> : "—"}{detailBill.zoho_bill_id && <span className="ml-1 font-mono text-xs text-zinc-500">{detailBill.zoho_bill_id}</span>}</div>
              </div>
              {/* Lines */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100">
                    <tr>
                      <th className="p-2 text-left font-mono text-xs">SKU</th>
                      <th className="p-2 text-right font-mono text-xs">Qty</th>
                      <th className="p-2 text-right font-mono text-xs">Unit ₹</th>
                      <th className="p-2 text-right font-mono text-xs">Master ₹</th>
                      <th className="p-2 text-left font-mono text-xs">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailBill.lines || []).map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-mono text-xs">{l.buyer_sku_id}<div className="text-zinc-500 text-xs">{l.sku_description}</div></td>
                        <td className="p-2 text-right font-mono">{fmt(l.qty)}</td>
                        <td className="p-2 text-right font-mono">{fmt(l.unit_price)}</td>
                        <td className="p-2 text-right font-mono text-zinc-500">{l.price_master_rate != null ? fmt(l.price_master_rate) : "—"}</td>
                        <td className="p-2">{l.variance_flag ? <StatusPill status={l.variance_flag}>{l.variance_pct?.toFixed?.(2)}%</StatusPill> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detailBill.notes && (<div className="text-sm text-zinc-600"><strong>Notes:</strong> {detailBill.notes}</div>)}
              {/* Cancel section */}
              {/* Cancel section — allow on any pre-terminal status */}
              {["DRAFT", "APPROVED", "POSTED", "PARTIAL"].includes(detailBill.status) && (
                <div className="border-t pt-3 flex justify-end">
                  <PermButton entity="FGInwardBill" action="DELETE" variant="destructive" onClick={() => setConfirmCancel(true)} className="gap-1" data-testid="fg-inward-cancel-btn">
                    <Trash2 className="w-4 h-4" /> Cancel Bill
                  </PermButton>
                </div>
              )}
              {detailBill.status === "REJECTED" && (
                <div className="p-3 border border-rose-200 bg-rose-50 rounded-lg text-sm text-rose-800" data-testid="fg-inward-rejected-banner">
                  <XCircle className="w-4 h-4 inline mr-1" />
                  <strong>REJECTED by Zoho</strong>
                  {detailBill.rejected_at && <> on {fmtDate(detailBill.rejected_at)}</>}
                  {detailBill.reject_reason && <>. Reason: <em>{detailBill.reject_reason}</em></>}
                  {detailBill.reversal_blocked && (
                    <div className="mt-2 p-2 border border-rose-300 bg-white rounded font-mono text-xs">
                      ⚠ FIFO setoff reversal was BLOCKED: {detailBill.reversal_blocking_links?.length || 0}
                      {" "}later invoice(s) already drew from the same pending intakes.
                      {" "}Cancel those first, then re-run reconciliation.
                    </div>
                  )}
                  {detailBill.reversal_blocked === false && detailBill.setoff_reversed && (
                    <div className="mt-1 text-xs text-rose-700 opacity-80">FIFO setoff was reversed automatically.</div>
                  )}
                </div>
              )}
              {detailBill.status === "CANCELLED" && (
                <div className="p-3 border border-zinc-200 bg-zinc-50 rounded-lg text-sm text-zinc-600">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" /> Cancelled by {detailBill.cancelled_by_name || detailBill.cancelled_by} on {fmtDate(detailBill.cancelled_at)}. Reason: <em>{detailBill.cancel_reason || "—"}</em>
                </div>
              )}
              {detailBill.status === "DELETED" && (
                <div
                  className="p-3 border border-zinc-300 bg-zinc-100 rounded-lg text-sm text-zinc-600"
                  data-testid="fg-inward-deleted-banner"
                >
                  <XCircle className="w-4 h-4 inline mr-1" />
                  Deleted by {detailBill.deleted_by_name || detailBill.deleted_by} on {fmtDate(detailBill.deleted_at)} from status <b>{detailBill.pre_delete_status}</b>
                  {detailBill.delete_reason && <>. Reason: <em>{detailBill.delete_reason}</em></>}
                </div>
              )}
              {/* Feb 2026 — draft-lifecycle actions */}
              {["PLAYFIELD_DRAFT", "PUSH_FAILED", "CANCELLED", "REJECTED"].includes(detailBill.status) && (
                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200 mt-2">
                  {detailBill.status === "PLAYFIELD_DRAFT" && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Prefill the create dialog with this draft's data.
                          setEditingDraftId(detailBill.id);
                          setForm({
                            bill_number: detailBill.bill_number || "",
                            order_number: detailBill.order_number || "",
                            bill_date: detailBill.bill_date || "",
                            due_date: detailBill.due_date || "",
                            payment_terms: detailBill.payment_terms || "DUE_ON_RECEIPT",
                            accounts_payable: detailBill.accounts_payable || "Trade Payables",
                            vendor_id: detailBill.vendor_id || "",
                            branch: detailBill.branch || "",
                            reverse_charge: !!detailBill.reverse_charge,
                            notes: detailBill.notes || "",
                            lines: (detailBill.lines || []).map(l => ({ ...l })),
                          });
                          setDetailBill(null);
                          setShowCreate(true);
                        }}
                        data-testid="fg-inward-edit-draft"
                      >
                        Edit draft
                      </Button>
                      <Button
                        onClick={handleSubmitDraft}
                        disabled={draftBusy}
                        data-testid="fg-inward-submit-draft"
                      >
                        {draftBusy ? "Submitting…" : "Submit to Zoho"}
                      </Button>
                    </>
                  )}
                  {detailBill.status === "PUSH_FAILED" && (
                    <Button
                      onClick={handleSubmitDraft}
                      disabled={draftBusy}
                      data-testid="fg-inward-retry-submit"
                      title="Retry Zoho push"
                    >
                      {draftBusy ? "Retrying…" : "Retry submit"}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={handleSoftDelete}
                    disabled={draftBusy}
                    data-testid="fg-inward-soft-delete"
                    title="Retire this bill — frees up the bill number"
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aging drill-down (Phase 4E) */}
      <Dialog open={!!agingDrill} onOpenChange={(open) => { if (!open) setAgingDrill(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fg-inward-aging-drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" /> Pending intakes — {agingDrill?.label}
            </DialogTitle>
          </DialogHeader>
          {agingDrill && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                {agingDrill.rows.length} {agingDrill.rows.length === 1 ? "row" : "rows"} of FG produced but
                not yet invoiced. Chase the vendor for these invoices, or create the FG-Inward bill yourself.
              </p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100">
                    <tr>
                      <th className="p-2 text-left font-mono text-xs">Buyer SKU</th>
                      <th className="p-2 text-left font-mono text-xs">Branch</th>
                      <th className="p-2 text-right font-mono text-xs">Qty remaining</th>
                      <th className="p-2 text-right font-mono text-xs">Days pending</th>
                      <th className="p-2 text-right font-mono text-xs">Master ₹</th>
                      <th className="p-2 text-right font-mono text-xs">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agingDrill.rows.map((r, i) => (
                      <tr key={r.movement_id || i} className="border-t">
                        <td className="p-2 font-mono text-xs">{r.buyer_sku_id}</td>
                        <td className="p-2 text-xs">{r.branch}</td>
                        <td className="p-2 text-right font-mono">{fmt(r.qty_remaining)}</td>
                        <td className="p-2 text-right font-mono">{r.days_pending}</td>
                        <td className="p-2 text-right font-mono text-zinc-500">{r.master_rate != null ? fmt(r.master_rate) : "—"}</td>
                        <td className="p-2 text-right font-mono">{r.value_at_master_rate != null ? `₹ ${fmt(r.value_at_master_rate)}` : <span className="text-amber-700">no rate</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-md" data-testid="fg-inward-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel Bill?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>This will reverse the FIFO setoff using line-level snapshots. Inventory will be restored exactly as it was before this bill posted. If a later bill drew from the same pending intakes, the cancel will be blocked — cancel the later bill first.</p>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} data-testid="fg-inward-cancel-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConfirmCancel(false)}>Keep Bill</Button>
              <Button variant="destructive" onClick={submitCancel} data-testid="fg-inward-cancel-confirm">Cancel Bill</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function _blankForm() {
  return {
    bill_number: "",
    // Feb 2026 — RM Inward parity: optional free-text vendor PO ref.
    order_number: "",
    bill_date: new Date().toISOString().slice(0, 10),
    due_date: new Date().toISOString().slice(0, 10),       // default Due on receipt
    payment_terms: "DUE_ON_RECEIPT",
    accounts_payable: "Trade Payables",
    vendor_id: "",
    branch: "",
    notes: "",
    lines: [{ buyer_sku_id: "", description: "", hsn: "", qty: "", unit_price: "", tax: "GST_18", tax_id: "", account_id: "", amount: 0 }],
    // Bill-level discount (mirrors RM Inward — bill-level only, 3a).
    discount_type: "percentage",
    discount_value: 0,
    // Bill-level TDS/TCS (RM Inward parity, Feb 2026). Local
    // `db.tds_taxes.id` or the sentinel "NONE".
    tds_tcs: "NONE",
  };
}

// RM Inward parity — same payment terms enum the RM dialog uses.
const PAYMENT_TERMS = [
  { value: "DUE_ON_RECEIPT", label: "Due on receipt",   days: 0  },
  { value: "NET_15",         label: "Net 15",            days: 15 },
  { value: "NET_30",         label: "Net 30",            days: 30 },
  { value: "NET_45",         label: "Net 45",            days: 45 },
  { value: "NET_60",         label: "Net 60",            days: 60 },
  { value: "CUSTOM",         label: "Custom",            days: null },
];

function formatErr(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.error) return d.error;
  return fallback;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
